import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  X, 
  User, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Award, 
  TrendingUp, 
  ExternalLink, 
  FileText, 
  Edit3, 
  ChevronDown, 
  ChevronUp, 
  Briefcase,
  AlertTriangle,
  RotateCcw,
  CheckCircle
} from 'lucide-react';
import { FaGithub } from 'react-icons/fa';

const STATUS_BADGES = {
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Submitted: 'bg-purple-50 text-purple-700 border-purple-200',
  'Under Review': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Changes Requested': 'bg-amber-50 text-amber-700 border-amber-200',
  Pending: 'bg-slate-100 text-slate-700 border-slate-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  Overdue: 'bg-rose-50 text-rose-700 border-rose-200'
};

const ATTENDANCE_BADGES = {
  Present: 'bg-emerald-500 text-white',
  Incomplete: 'bg-rose-500 text-white',
  Absent: 'bg-rose-500 text-white',
  'No Assignment': 'bg-slate-400 text-white',
  Late: 'bg-amber-500 text-white',
  Leave: 'bg-blue-500 text-white',
  Holiday: 'bg-purple-500 text-white'
};

const UserPerformanceModal = ({ isOpen, onClose, userId, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [reviewModalTask, setReviewModalTask] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('Completed');
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserHistory();
    }
  }, [isOpen, userId]);

  const fetchUserHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const res = await axios.get(`${window.API_URL}/api/portal/admin/daily-assignments/user/${userId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      // Auto-expand the most recent day
      if (res.data.history && res.data.history.length > 0) {
        setExpandedDay(res.data.history[0].date);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to load user performance history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOverrideAttendance = async (date, currentStatus) => {
    const { value: formValues } = await Swal.fire({
      title: 'Override Attendance',
      html: `
        <div style="text-align: left; font-size: 13px;">
          <label style="font-weight: bold; display: block; margin-bottom: 4px;">Date: ${date}</label>
          <label style="font-weight: bold; display: block; margin-top: 10px; margin-bottom: 4px;">New Status:</label>
          <select id="swal-status" class="swal2-input" style="width: 100%; margin: 0 0 10px 0; font-size: 13px;">
            <option value="Present" ${currentStatus === 'Present' ? 'selected' : ''}>Present</option>
            <option value="Incomplete" ${currentStatus === 'Incomplete' ? 'selected' : ''}>Incomplete / Absent</option>
            <option value="Leave" ${currentStatus === 'Leave' ? 'selected' : ''}>Approved Leave</option>
            <option value="Holiday" ${currentStatus === 'Holiday' ? 'selected' : ''}>Holiday / Day Off</option>
          </select>
          <label style="font-weight: bold; display: block; margin-top: 10px; margin-bottom: 4px;">Audit Reason (Required):</label>
          <input id="swal-reason" class="swal2-input" placeholder="e.g. Approved medical leave adjustment" style="width: 100%; margin: 0; font-size: 13px;" />
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: '#f06600',
      preConfirm: () => {
        const status = document.getElementById('swal-status').value;
        const reason = document.getElementById('swal-reason').value;
        if (!reason || !reason.trim()) {
          Swal.showValidationMessage('An explicit reason is required for audit trail.');
          return false;
        }
        return { status, reason };
      }
    });

    if (formValues) {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        await axios.put(`${window.API_URL}/api/portal/admin/attendance/override`, {
          userId,
          date,
          status: formValues.status,
          reason: formValues.reason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: 'success',
          title: 'Attendance Overridden!',
          text: `Attendance for ${date} updated to ${formValues.status}.`,
          toast: true,
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false
        });

        fetchUserHistory();
        if (onRefresh) onRefresh();
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Failed to override attendance', 'error');
      }
    }
  };

  const handleReviewTaskSubmit = async (e) => {
    e.preventDefault();
    if (!reviewModalTask) return;
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      await axios.put(`${window.API_URL}/api/portal/admin/tasks/${reviewModalTask._id}/review`, {
        status: reviewStatus,
        feedback: reviewFeedback
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      Swal.fire({
        icon: 'success',
        title: `Task marked as ${reviewStatus}!`,
        toast: true,
        position: 'top-end',
        timer: 2000,
        showConfirmButton: false
      });

      setReviewModalTask(null);
      setReviewFeedback('');
      fetchUserHistory();
      if (onRefresh) onRefresh();
    } catch (err) {
      Swal.fire('Error', 'Failed to update review status', 'error');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (!isOpen) return null;

  const user = data?.user || {};
  const stats = data?.stats || {};
  const history = data?.history || [];

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[94vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-between shrink-0 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#f06600] to-orange-400 text-white flex items-center justify-center font-black text-lg shadow-lg shadow-orange-500/25">
              {user.fullName ? user.fullName.charAt(0).toUpperCase() : <User />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                  {user.fullName || 'Team Member Performance'}
                </h3>
                <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-orange-400 text-xs font-bold border border-orange-500/30">
                  {user.role || 'Member'}
                </span>
                {user.fieldOrDepartment && (
                  <span className="text-xs text-slate-400">
                    • {user.fieldOrDepartment}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {user.email} • {user.phoneNumber || 'No phone'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="w-10 h-10 rounded-full border-3 border-[#f06600] border-t-transparent animate-spin mx-auto"></div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loading performance data...</p>
            </div>
          ) : (
            <>
              {/* Overall Summary Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-slate-500 font-semibold mb-0.5">Total Tasks</div>
                  <div className="text-xl sm:text-2xl font-black text-slate-900">{stats.totalAssignedTasks || 0}</div>
                </div>

                <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-emerald-700 font-semibold mb-0.5">Completed</div>
                  <div className="text-xl sm:text-2xl font-black text-emerald-700">{stats.totalCompletedTasks || 0}</div>
                </div>

                <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-amber-700 font-semibold mb-0.5">Pending</div>
                  <div className="text-xl sm:text-2xl font-black text-amber-700">{stats.totalPendingTasks || 0}</div>
                </div>

                <div className="bg-orange-50/70 border border-orange-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-[#f06600] font-semibold mb-0.5">Task Comp. Rate</div>
                  <div className="text-xl sm:text-2xl font-black text-[#f06600]">{stats.overallCompletionRate || 0}%</div>
                </div>

                <div className="bg-blue-50/70 border border-blue-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-blue-700 font-semibold mb-0.5">Present Days</div>
                  <div className="text-xl sm:text-2xl font-black text-blue-700">{stats.presentDays || 0}</div>
                </div>

                <div className="bg-rose-50/70 border border-rose-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-rose-700 font-semibold mb-0.5">Incomplete Days</div>
                  <div className="text-xl sm:text-2xl font-black text-rose-700">{stats.absentDays || 0}</div>
                </div>

                <div className="bg-purple-50/70 border border-purple-200 rounded-2xl p-3.5 text-center">
                  <div className="text-xs text-purple-700 font-semibold mb-0.5">Attendance Rate</div>
                  <div className="text-xl sm:text-2xl font-black text-purple-700">{stats.attendanceRate || 0}%</div>
                </div>
              </div>

              {/* Day-by-Day Historical Accordion */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#f06600]" />
                    <span>Daily History &amp; Task Breakdown</span>
                    <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                      {history.length} Days Recorded
                    </span>
                  </h4>
                  <span className="text-[11px] text-slate-500">
                    Click any day to view complete tasks, submissions &amp; attendance controls
                  </span>
                </div>

                {history.length === 0 ? (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-200 text-slate-500 text-xs">
                    No task assignments or attendance history found for this user yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((day) => {
                      const isExpanded = expandedDay === day.date;
                      return (
                        <div
                          key={day.date}
                          className="bg-white border-2 border-slate-200 hover:border-slate-300 rounded-2xl overflow-hidden transition-all shadow-sm"
                        >
                          {/* Day Row Header */}
                          <div
                            onClick={() => setExpandedDay(isExpanded ? null : day.date)}
                            className="p-4 sm:p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/80 transition-colors flex-wrap"
                          >
                            {/* Left: Day & Date */}
                            <div className="flex items-center gap-3">
                              <span className="w-10 h-10 rounded-xl bg-slate-900 text-white font-black text-xs flex items-center justify-center shrink-0">
                                Day {day.internshipDay}
                              </span>
                              <div>
                                <h5 className="font-extrabold text-sm text-slate-900 tracking-tight">
                                  {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                </h5>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                  <span>{day.completedTasks} of {day.totalTasks} Tasks Completed</span>
                                  <span>•</span>
                                  <span className="font-bold text-slate-700">{day.completionRate}%</span>
                                </div>
                              </div>
                            </div>

                            {/* Middle: Progress Bar */}
                            <div className="w-36 sm:w-44 hidden md:block">
                              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                <div
                                  className={`h-full transition-all duration-500 ${day.completionRate === 100 ? 'bg-emerald-500' : day.completionRate >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                  style={{ width: `${day.completionRate}%` }}
                                ></div>
                              </div>
                            </div>

                            {/* Right: Attendance Badge & Controls */}
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-right">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm inline-block ${ATTENDANCE_BADGES[day.attendanceStatus] || 'bg-slate-600 text-white'}`}>
                                  {day.attendanceStatus}
                                </span>
                                {day.isOverridden && (
                                  <span className="block text-[10px] text-amber-600 font-bold mt-0.5">
                                    ★ Admin Override
                                  </span>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOverrideAttendance(day.date, day.attendanceStatus);
                                }}
                                title="Override Attendance"
                                className="px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:text-[#f06600] bg-slate-100 hover:bg-orange-50 border border-slate-200 rounded-lg transition-colors flex items-center gap-1"
                              >
                                <Edit3 className="w-3 h-3" />
                                <span className="hidden sm:inline">Override</span>
                              </button>

                              <div className="text-slate-400">
                                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Task Details */}
                          {isExpanded && (
                            <div className="border-t border-slate-200 bg-slate-50/50 p-4 sm:p-5 space-y-4">
                              {day.isOverridden && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
                                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                  <div>
                                    <span className="font-bold">Admin Override Active: </span>
                                    <span>Reason: "{day.overrideReason}"</span>
                                  </div>
                                </div>
                              )}

                              <div className="space-y-3">
                                {day.tasks.map((task) => (
                                  <div
                                    key={task._id}
                                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-2.5"
                                  >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                          <span className="font-extrabold text-sm text-slate-900">
                                            {task.title}
                                          </span>
                                          <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold">
                                            {task.category || 'General'}
                                          </span>
                                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${STATUS_BADGES[task.status] || 'bg-slate-100'}`}>
                                            {task.status}
                                          </span>
                                        </div>
                                        {task.description && (
                                          <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                                            {task.description}
                                          </p>
                                        )}
                                      </div>

                                      {/* Action button to Review / Approve */}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setReviewModalTask(task);
                                          setReviewStatus(task.status === 'Completed' ? 'Completed' : 'Completed');
                                          setReviewFeedback(task.feedback || '');
                                        }}
                                        className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-[#f06600] text-white text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
                                      >
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>Review / Grade</span>
                                      </button>
                                    </div>

                                    {/* Submission Details if submitted */}
                                    {(task.submittedFiles?.length > 0 || task.githubLink || task.liveLink || task.submissionNotes) && (
                                      <div className="mt-2 pt-2 border-t border-slate-100 bg-slate-50/80 rounded-lg p-3 text-xs space-y-2">
                                        <div className="font-bold text-slate-700 flex items-center gap-1.5">
                                          <FileText className="w-3.5 h-3.5 text-[#f06600]" />
                                          <span>Intern Submission:</span>
                                        </div>

                                        {task.submissionNotes && (
                                          <p className="text-slate-700 whitespace-pre-wrap bg-white p-2 rounded border border-slate-200">
                                            {task.submissionNotes}
                                          </p>
                                        )}

                                        <div className="flex items-center gap-2 flex-wrap">
                                          {task.githubLink && (
                                            <a
                                              href={task.githubLink}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="px-2.5 py-1 bg-slate-900 text-white rounded text-xs font-semibold flex items-center gap-1"
                                            >
                                              <FaGithub /> GitHub Repo
                                            </a>
                                          )}
                                          {task.liveLink && (
                                            <a
                                              href={task.liveLink}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="px-2.5 py-1 bg-[#f06600] text-white rounded text-xs font-semibold flex items-center gap-1"
                                            >
                                              <ExternalLink className="w-3 h-3" /> Live Demo
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Admin Feedback Display if present */}
                                    {task.feedback && (
                                      <div className="mt-1 p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-900">
                                        <span className="font-bold">Admin Feedback: </span>
                                        <span>{task.feedback}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold transition-colors cursor-pointer"
          >
            Close Performance Overview
          </button>
        </div>

      </div>

      {/* Review / Feedback Sub-Modal */}
      {reviewModalTask && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4 shadow-2xl border border-slate-200 animate-in fade-in">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-base text-slate-900">
                Review Task: "{reviewModalTask.title}"
              </h4>
              <button
                onClick={() => setReviewModalTask(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleReviewTaskSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Task Status Decision *
                </label>
                <select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm font-bold text-slate-800"
                >
                  <option value="Completed">✅ Approve / Completed</option>
                  <option value="Changes Requested">⚠️ Request Changes</option>
                  <option value="Rejected">❌ Reject</option>
                  <option value="In Progress">🟡 In Progress</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Feedback &amp; Instructions to Intern:
                </label>
                <textarea
                  rows={3}
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                  placeholder="Provide constructive feedback or specify changes needed..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-800 leading-relaxed"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setReviewModalTask(null)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReview}
                  className="px-5 py-2 text-xs font-bold text-white bg-[#f06600] hover:bg-orange-600 rounded-xl shadow"
                >
                  {submittingReview ? 'Saving...' : 'Save Decision'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserPerformanceModal;
